/**
 * The block catalog as React components — one per manifest, driven entirely by the
 * manifest's declared props and binding slots.
 *
 * CLAUDE.md rule 4 forbids bespoke components *per report format*; a component per
 * *block type* is the opposite of that, and is what makes formats declarative. The
 * test to apply when adding anything here: could this component name a specific
 * client, campaign or report? If so it is in the wrong place.
 *
 * Nothing in this file reads the report data model directly. Components receive
 * already-evaluated values, so they are pure presentation and can be exercised from
 * fixtures with no evaluator in the loop.
 */

import * as React from 'react';
import type { BlockManifest } from '@pdmg/reporting-schema';
import { formatInt, formatPct, formatDec2, renderMetric } from './format.js';
import { SeriesChartSvg, PieChartSvg } from './charts.js';
import { Icon, IconBadge, DeviceIllustration, IconStyleContext } from './icons.js';
import { BrowserLogo, normaliseBrowser } from './browser-logos.js';
import { BROWSER_LOGO_IMAGES } from './browser-logo-images.js';
import { REPORT_ICON_IMAGES } from './report-icon-images.js';
import { buildGeoRender, buildStateChoropleth, scopeCitiesToTarget, GEO_MODES, type GeoMode } from './geo-map.js';
import type { BindingSource } from '@pdmg/reporting-bindings';

export interface BlockProps {
  props: Record<string, unknown>;
  values: Record<string, unknown>;
  manifest: BlockManifest;
  children?: React.ReactNode;
}

/**
 * Resolves an assetId to an inlined data URI. Returning null renders a placeholder.
 *
 * Never a URL: the render service disables network at print time (R4), so an asset
 * that isn't inlined by this point cannot appear at all. P0-4 supplies the real
 * implementation; M1 renders placeholders and says so.
 */
export type AssetResolver = (assetId: string) => string | null;
export const AssetContext = React.createContext<AssetResolver>(() => null);

// ------------------------------------------------------------- editing --

/**
 * In-line editing, as an ANNOTATION rather than as behaviour.
 *
 * The builder edits a report by overriding a value in the report DATA and re-deriving.
 * So a block's only job here is to say "this piece of output came from that data path";
 * the editor UI, the input, the validation and the save all live in the web app. Blocks
 * that knew how to edit themselves would put format-specific behaviour back into the
 * catalog, which is the coupling this project exists to remove (C2, rule 4).
 *
 * ── Why the default renders nothing at all ──
 *
 * R4 requires byte-reproducible PDFs. `EditContext` is off during SSR and `<V>` then
 * returns its children untouched — not a span with no attributes, literally the
 * children — so the print path emits exactly the markup it emitted before this existed.
 * There is an assertion for that in the suite; a wrapper element "that shouldn't matter"
 * would change the measured height of a block and move a page break.
 */
export interface EditAnnotation {
  /** Data path -> the override that is already pinned there, if any. */
  overridden: (dataPath: string) => boolean;
  /**
   * Reconciliation status for the LAST-changed figure of a summing group (device category
   * mobile+desktop, or the browser breakdown): 'ok' when the group now sums to total clicks — shown
   * green — or 'bad' when it does not — shown red. Any other edited figure just reads as 'set' (amber).
   */
  reconcile?: (dataPath: string) => 'ok' | 'bad' | undefined;
}

export const EditContext = React.createContext<EditAnnotation | null>(null);

/**
 * Binding provenance for the block being rendered, supplied by `<Document>`.
 * Empty outside a document, which is what makes `<V>` inert in isolation.
 */
export const SourceContext = React.createContext<Record<string, BindingSource>>({});

/**
 * Marks a rendered value as coming from a binding slot.
 *
 *   <V slot="ctr">{renderMetric(values.ctr, u, formatPct)}</V>
 *   <V slot="tiles" leaf={`[${i}].value`}>…</V>
 *
 * `leaf` addresses one value inside a structure-building binding — the page-1 KPI tiles
 * are an array literal, so without it the four headline figures would have no path and
 * could not be edited.
 */
/**
 * The data path behind one rendered value.
 *
 * A leaf resolves two different ways, because a binding can produce a structure two
 * different ways:
 *
 *   rows: "overall.devices"                       a path TO a collection
 *     -> "[2].clicks" appends: overall.devices[2].clicks
 *
 *   tiles: "[{\"value\": overall.delivery.clicks}]"  a literal that BUILDS one
 *     -> "[0].value" is looked up: overall.delivery.clicks
 *
 * Only the second case has recorded leaf paths, because only there does the position in
 * the output differ from the position in the data. Handling just that case left every
 * table in the catalog uneditable — which is most of the report.
 */
function pathOf(src: BindingSource, leaf?: string): string | null {
  if (leaf === undefined) return src.dataPath;
  if (src.dataPath !== null) return leaf.startsWith('[') ? `${src.dataPath}${leaf}` : `${src.dataPath}.${leaf}`;
  return src.leafPaths[leaf] ?? null;
}

export function V(
  { slot, leaf, children }: { slot: string; leaf?: string; children?: React.ReactNode }
): React.ReactElement {
  const edit = React.useContext(EditContext);
  const sources = React.useContext(SourceContext);
  if (!edit) return <>{children}</>;

  const src = sources[slot];
  const path = src ? pathOf(src, leaf) : null;
  // A computed binding has no single place to put an answer, so it gets no affordance.
  // Offering one would write to a path the report never reads: the figure would appear
  // to change and then revert on the next render.
  if (!path) return <>{children}</>;

  // A reconciling group's last-changed figure reads green (sums to total) or red (does not); every
  // other edited figure reads amber (`ev-set`).
  const rec = edit.reconcile?.(path);
  const mark = rec === 'ok' ? ' ev-ok' : rec === 'bad' ? ' ev-bad' : edit.overridden(path) ? ' ev-set' : '';
  return (
    <span
      className={'ev' + mark}
      data-edit-path={path}
      data-edit-slot={slot}
      tabIndex={0}
      role="button"
    >
      {children}
    </span>
  );
}

const s = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const arr = (v: unknown): any[] => (Array.isArray(v) ? v : v === null || v === undefined ? [] : [v]);

/** Asset slot: an inlined image, or a labelled placeholder of the right size. */
function Asset({ assetId, heightIn, label, fill }: { assetId: string; heightIn: number; label: string; fill?: boolean }) {
  const resolve = React.useContext(AssetContext);
  // A `data:` URI (a map or creative inlined at generation) or an http(s) URL is already a usable image
  // src — render it directly. Passing it through `resolve` (which looks up stored asset IDs) returns
  // null, and the placeholder below then prints the whole URI as text — the "garbled text, no image".
  const direct = assetId && /^(data:|https?:)/i.test(assetId) ? assetId : null;
  const src = direct ?? (assetId ? resolve(assetId) : null);
  // `fill`: the image fills the box width at a fixed height and crops to its TOP (object-position top),
  // so a tall portrait (an email deployment capture) shows its top, zoomed, rather than sitting small and
  // letter-boxed. The default keeps the whole image, scaled to fit within the height and centred.
  if (src) return <img src={src} alt="" style={fill
    ? { width: '100%', height: `${heightIn}in`, objectFit: 'cover', objectPosition: 'top', display: 'block' }
    : { maxWidth: '100%', maxHeight: `${heightIn}in`, display: 'block', margin: '0 auto' }} />;
  return (
    <div className="asset-placeholder" style={{ height: `${heightIn}in` }}>
      {label}
    </div>
  );
}

// ---------------------------------------------------------------- chrome --

const PageHeader = ({ props, values }: BlockProps) => {
  // The white-label logo: a data: URI inlined at generation, drawn top-left above the title. Shown
  // only when a logo is present AND showLogo is on (the reviewer's checkbox, default on). An agency
  // with no logo, or a report with the toggle off, renders the header exactly as before.
  const logo = s(values.logo);
  const showLogo = values.showLogo !== false;
  return (
    <div className="hdr">
      <div>
        {showLogo && logo ? <img className="hdr-logo" src={logo} alt="" /> : null}
        <div className="hdr-title">{s(props.title)}</div>
        {props.showAccentRule !== false && <div className="hdr-rule" />}
      </div>
      <div className="hdr-meta">
        <div><V slot="clientName">{s(values.clientName)}</V></div>
        <div className="cid"><span className="cid-label">{s(props.campaignIdLabel) || 'CAMPAIGN ID#:'}</span> <V slot="campaignId">{s(values.campaignId)}</V></div>
      </div>
    </div>
  );
};

/**
 * The in-flow footer block carries the copyright only.
 *
 * "Page N of M" is deliberately absent: page numbers are injected by the render
 * service into the PDF's margin box, where the browser resolves them against the real
 * paginated total. A footer block cannot know M, and the mockups prove what happens
 * when someone tries — two of the three number their pages wrongly (spec C4).
 */
/**
 * Renders nothing in the document flow.
 *
 * The footer line is drawn by the render service into the page margin box, because only
 * that box knows `pageNumber` and `totalPages` — and the mockups put the notice and the
 * page number together on one centred line. Emitting it here too would print it twice.
 *
 * The block is kept rather than removed: it is where the template declares `year` and
 * `companyName`, and the render service reads those values off it to build the line. Its
 * job is to carry the binding, not to paint.
 */
const PageFooter = (_props: BlockProps) => null;

// -------------------------------------------------------------- identity --

/**
 * Normalise a campaign date to MM/DD/YYYY, dropping any timestamp.
 *
 * The source dates arrive as "YYYY-MM-DD HH:MM:SS" (the ad-group flight times). No `new Date()` — that
 * would shift the day across a timezone; this is pure string reshaping. US "M/D/YYYY" is accepted and
 * re-padded; anything else just has a trailing time stripped.
 */
const fmtDate = (v: unknown): string => {
  const raw = s(v).trim();
  if (!raw) return '';
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[2].padStart(2, '0')}/${m[3].padStart(2, '0')}/${m[1]}`;
  m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`;
  return raw.replace(/[ T]\d{1,2}:\d{2}.*$/, '');
};

const CampaignMetaCard = ({ props, values }: BlockProps) => {
  // V2 reports carry the approved raster marks; legacy keeps the inline-SVG glyphs the mockups used.
  const v2 = React.useContext(IconStyleContext) === 'v2';
  const ic = { client: v2 ? 'meta-client' : 'person-solid', date: v2 ? 'meta-start' : 'calendar-solid',
    end: v2 ? 'meta-end' : 'calendar-solid', id: v2 ? 'meta-campaign-id' : 'hash-solid',
    // Campaign name is campaign identity, like the ID — on a V2 report it takes the same raster mark so
    // it matches the rest of the card instead of falling back to a small legacy glyph.
    // `nameIcon` lets a template give the name row its own mark where both rows show (CTV), so the card
    // does not carry two identical "#" badges.
    name: s(props.nameIcon) || (v2 ? 'meta-campaign-id' : 'image') };
  /*
   * Row order follows the mockup, and the optional campaign-name row is what makes this
   * usable for DOOH pre-flight without a second block: display and audio identify a
   * campaign by client and ID, while the pre-flight sheet names it too.
   */
  const rows: Array<[string, string, string, string]> = [
    // Labelled "Agency" (not "Client") per the agency white-label reports; the value is still the
    // clientName field — only the row's label changed.
    [ic.client, 'Agency', 'clientName', s(values.clientName)],
    ...(values.campaignName
      ? [[ic.name, 'Campaign Name', 'campaignName', s(values.campaignName)] as [string, string, string, string]]
      : []),
    [ic.date, 'Campaign Start', 'startDate', fmtDate(values.startDate)],
    [ic.end, 'Campaign End', 'endDate', fmtDate(values.endDate)],
    [ic.id, s(props.campaignIdLabel) || 'Campaign ID', 'campaignId', s(values.campaignId)],
  ];
  // The note is authored on the block, not bound, because it is a standing caveat about
  // what a pre-flight report is rather than a figure that varies per campaign.
  const note = s(props.note) || (props.showNote === true ? s(values.note) : '');
  return (
    <div className={v2 ? 'card meta-card meta-card-v2' : 'card meta-card'}>
      <div className="card-body">
        <dl className="meta-rows">
          {rows.map(([icon, label, slot, value]) => (
            <div className="meta-row" key={label}>
              {/* An outlined ring, not a filled disc — the mockup's treatment here. */}
              <span className="icon-badge ring"><Icon name={icon} size={v2 ? 30 : 15} /></span>
              <dt>{label}:</dt>
              <dd><V slot={slot}>{value}</V></dd>
            </div>
          ))}
        </dl>
        {note ? <p className="meta-note"><strong>Note:</strong> {note}</p> : null}
      </div>
    </div>
  );
};

const CreativePreview = ({ props, values }: BlockProps) => {
  // `hideWhenEmpty`: no artwork at all (a dead creative link) drops the box instead of printing an empty
  // frame with only the "representative creative" caption under nothing.
  if (props.hideWhenEmpty === true && !s(values.assetId)) return null;
  // `bare`: the artwork floats on the page with no card frame/background/shadow (the V2 identity creative).
  const bare = props.bare === true;
  const body = (
    <>
      <Asset assetId={s(values.assetId)} heightIn={n(props.maxHeightIn) ?? 2.4} label="creative" fill={props.fillTop === true} />
      {/* "Representative of creative used in campaign" — the mockup is explicit that the
          artwork shown is illustrative, which matters most on a pre-flight report. */}
      {props.caption ? <p className="creative-caption">{s(props.caption)}</p> : null}
      {props.footnote ? <div className="tiny muted" style={{ marginTop: '0.06in' }}>{s(props.footnote)}</div> : null}
    </>
  );
  return bare
    ? <div className="creative-bare">{body}</div>
    : <div className="card"><div className="card-body">{body}</div></div>;
};

// ---------------------------------------------------------------- metric --

const KpiTileRow = ({ props, values, manifest }: BlockProps) => {
  const tiles = arr(values.tiles) as Array<{ label?: string; value?: unknown; icon?: string; format?: string }>;
  const columns = n(props.columns) ?? 4;
  return (
    <div className="kpi" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {tiles.map((t, i) => (
        <div className="kpi-tile" key={i}>
          {/*
            Label, then icon, then value — the mockup's order, and not the obvious one.
            Reading top to bottom you learn WHAT you are looking at before you read the
            number, which is why the label leads. The first build had value-then-label
            and read as a dashboard rather than a report.
          */}
          <div className="kpi-label">{s(t.label)}</div>
          <IconBadge name={t.icon} />
          {/*
            `format` matches MetricTrio's convention. Without it every tile formats as
            an integer, so a tile bound to a rate renders 18.5 as "19" — quietly wrong
            in a way that looks fine. Email surfaced this: its per-deployment tiles mix
            counts and rates in one row, which the display mockup never does.
          */}
          <div className="kpi-value num">
            <V slot="tiles" leaf={`[${i}].value`}>
              {renderMetric(t.value, manifest.unavailableRendering,
                t.format === 'pct' ? (x) => formatPct(x) : t.format === 'dec2' ? formatDec2 : formatInt)}
            </V>
          </div>
        </div>
      ))}
    </div>
  );
};

const DeliveryStrip = ({ props, values, manifest }: BlockProps) => {
  const u = manifest.unavailableRendering;
  // V2 sets the contract labels bold / dark blue to match their figures; legacy keeps the muted caption.
  const v2 = React.useContext(IconStyleContext) === 'v2';
  // Generic mode: a bound `metrics` array ([{label, value, format}]) drives the cells, so the band can
  // carry any figures — the email summary uses it for Targeted Records / Total Website Clicks. Without it,
  // the display contract cells (Contracted / Over-Delivered / Over-Delivery / CTR), bound field by field.
  const metrics = arr(values.metrics) as Array<{ label?: string; value?: unknown; format?: string }>;
  const cells: Array<{ label: string; node: React.ReactNode }> = metrics.length
    ? metrics.map((m, i) => ({
        label: s(m.label),
        node: (
          <V slot="metrics" leaf={`[${i}].value`}>
            {renderMetric(m.value, u, m.format === 'pct' ? (x) => formatPct(x) : m.format === 'dec2' ? formatDec2 : formatInt)}
          </V>
        ),
      }))
    : [
        { label: 'Contracted Impressions', node: <V slot="contracted">{renderMetric(values.contracted, u)}</V> },
        // V2 names the figure in full (it may wrap to two lines; the strip centres every cell vertically).
        { label: v2 ? 'Over-Delivered Impressions' : 'Over-Delivered', node: <V slot="overDelivered">{renderMetric(values.overDelivered, u)}</V> },
        { label: 'Over-Delivery', node: <V slot="overDeliveryPct">{renderMetric(values.overDeliveryPct, u, (x) => formatPct(x))}</V> },
        { label: 'CTR*', node: <V slot="ctr">{renderMetric(values.ctr, u, (x) => formatPct(x))}</V> },
      ];
  return (
    <div>
      <div className={v2 ? 'strip strip-v2' : 'strip'}>
        {cells.map((c, i) => (
          <div key={i}>
            <div className="strip-value num">{c.node}</div>
            <div className={v2 ? 'strip-label' : 'tiny muted'}>{c.label}</div>
          </div>
        ))}
      </div>
      {props.ctrFootnote ? <div className={v2 ? 'footnote' : 'tiny muted'} style={{ marginTop: '0.05in' }}>* {s(props.ctrFootnote)}</div> : null}
    </div>
  );
};

const MetricTrio = ({ values, manifest }: BlockProps) => {
  const metrics = arr(values.metrics) as Array<{ label?: string; value?: unknown; format?: string; icon?: string }>;
  // A trio with icons (audio's Ads Played / Reach / LTR) reads as titled figures like the KPI row,
  // so it keeps the tall two-line label box and the badge. A badgeless trio (email's rate
  // percentages) has single-line labels and no badge — the forced label height is dead space there,
  // so it renders compact, which is what keeps the email summary on one fixed sheet.
  const hasIcon = metrics.some((m) => m.icon);
  return (
    <div className={`kpi${hasIcon ? '' : ' kpi-compact'}`} style={{ gridTemplateColumns: `repeat(${metrics.length || 3}, 1fr)` }}>
      {metrics.map((m, i) => (
        // Same tile as the KPI row — label, then icon badge, then value — so the audio summary's
        // Ads Played / Reach / LTR read as titled figures, the way the mockup sets them.
        <div className="kpi-tile" key={i}>
          <div className="kpi-label">{s(m.label)}</div>
          {m.icon ? <IconBadge name={s(m.icon)} /> : null}
          <div className="kpi-value num">
            <V slot="metrics" leaf={`[${i}].value`}>
              {renderMetric(m.value, manifest.unavailableRendering,
                m.format === 'pct' ? (x) => formatPct(x) : m.format === 'dec2' ? formatDec2 : formatInt)}
            </V>
          </div>
        </div>
      ))}
    </div>
  );
};

const ContractedVsActual = ({ props, values, manifest }: BlockProps) => {
  // Icons are configurable so the DOOH report can carry its billboard marks while CTV keeps the generic
  // eye; default is the flat SVG eye, unchanged for any template that doesn't set them.
  const contractedIcon = s(props.contractedIcon) || 'eye';
  const actualIcon = s(props.actualIcon) || 'eye';
  return (
    <div className="strip">
      <div>
        <div className="cva-value"><IconBadge name={contractedIcon} size={15} /><span className="strip-value num">
          <V slot="contracted">{renderMetric(values.contracted, manifest.unavailableRendering)}</V>
        </span></div>
        <div className="tiny muted">CONTRACTED IMPRESSIONS</div>
      </div>
      <div>
        <div className="cva-value"><IconBadge name={actualIcon} size={15} /><span className="strip-value num">
          <V slot="actual">{renderMetric(values.actual, manifest.unavailableRendering)}</V>
        </span></div>
        <div className="tiny muted">ACTUAL IMPRESSIONS</div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- breakdown --

const DeviceCategoryCard = ({ props, values, manifest }: BlockProps) => {
  const v2 = React.useContext(IconStyleContext) === 'v2';
  const artPx = n(props.artHeightPx) ?? 46;
  return (
  <div className={v2 ? 'card card-framed breakdown-card' : 'card'}>
    <div className="card-title">{v2 ? <Icon name="banner-device-category" size={22} /> : <Icon name="devices" size={13} />}<span>Device Category</span></div>
    {/*
      * Illustration left, figures right — the mockup's arrangement.
      *
      * The two numbers were side by side as equal tiles, which read as a comparison of
      * two independent measures. They are a split of one total, and a labelled pair
      * beside a single device illustration says that; two centred tiles do not.
      */}
    <div className="card-body device-cat">
      {/*
        * The illustration is sized by the block, not fixed, because this card appears on
        * pages of very different density: it is one of four items on the display summary
        * and one of eight on the email one, where the same 46px pushed the page over its
        * fixed height.
        */}
      <div className="device-art">{v2 ? <Icon name="device-illustration" size={Math.round(artPx * 1.4)} /> : <DeviceIllustration height={artPx} />}</div>
      <dl className="device-split">
        <div><dt>Mobile</dt><dd className="num">
          <V slot="mobile">{renderMetric(values.mobile, manifest.unavailableRendering)}</V>
        </dd></div>
        <div><dt>Desktop</dt><dd className="num">
          <V slot="desktop">{renderMetric(values.desktop, manifest.unavailableRendering)}</V>
        </dd></div>
      </dl>
    </div>
  </div>
  );
};

/**
 * Device details. These sum to MOBILE clicks, not to total — see scope §4.1 and the
 * invariant table in the metric spec. The caption says so, because a table of device
 * counts sitting under a total-clicks figure invites exactly the wrong reading.
 */
const DeviceDetailsTable = ({ props, values }: BlockProps) => {
  const rows = arr(values.rows) as Array<{ name?: string; clicks?: number }>;
  const max = n(props.maxRows) ?? 8;
  const shown = rows.slice(0, max);
  const rest = rows.slice(max).reduce((t, r) => t + (r.clicks ?? 0), 0);
  // Title and the "sum to mobile clicks" footnote are configurable so the eblast page-1 table can read
  // "Mobile Device Details" with no footnote, while display/audio keep the defaults unchanged.
  const title = s(props.title) || 'Device Details';
  const showNote = props.showNote === undefined ? true : Boolean(props.showNote);
  const v2 = React.useContext(IconStyleContext) === 'v2';
  return (
    <div className={v2 ? 'card card-framed breakdown-card' : 'card'}>
      <div className="card-title">{v2 ? <><Icon name="banner-device-details" size={22} /><span>{title}</span></> : title}</div>
      <div className="card-body">
        <table>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                <td><V slot="rows" leaf={`[${i}].name`}>{s(r.name)}</V></td>
                <td className="n"><V slot="rows" leaf={`[${i}].clicks`}>{formatInt(r.clicks ?? 0)}</V></td>
              </tr>
            ))}
            {rest > 0 && <tr><td>Other</td><td className="n">{formatInt(rest)}</td></tr>}
          </tbody>
        </table>
        {showNote && <div className={v2 ? 'footnote' : 'tiny muted'} style={{ marginTop: '0.05in' }}>Device details sum to mobile clicks.</div>}
      </div>
    </div>
  );
};

/** Map a browser name to a logo image. `v2` picks the new raster marks; legacy keeps the approved PNGs. */
const browserImageFor = (name: string, v2: boolean): string => {
  // Reuse the canonical normaliser the drawn marks use, so the image path resolves the same aliases:
  // WebKit → Safari, Chromium → Chrome, "MS Edge" → Edge, etc.
  const key = normaliseBrowser(name);
  if (v2) {
    // The V2 raster browser marks (chrome/edge/firefox/safari); everything else → the "Other" mark.
    const slot: Record<string, string> = {
      chrome: 'browser-chrome', edge: 'browser-edge', firefox: 'browser-firefox', safari: 'browser-safari',
    };
    return REPORT_ICON_IMAGES[slot[key] ?? 'browser-other'];
  }
  // Legacy: the approved eblast PNG marks; an unmatched browser falls back to the generic 'web' globe.
  return BROWSER_LOGO_IMAGES[key] ?? BROWSER_LOGO_IMAGES.web;
};

const BrowserTypeCard = ({ props, values }: BlockProps) => {
  // Show EVERY browser row, not a top-N slice. The browser breakdown is reconciled to total website
  // clicks and balanced by hand, so a dropped tail row (e.g. a small "Unknown" bucket) would be summed
  // by the reconcile yet invisible to the reviewer — the section could never be made to balance. (The
  // `maxItems` prop is kept for compatibility but no longer truncates.)
  const rows = arr(values.rows) as Array<{ name?: string; clicks?: number }>;
  const v2 = React.useContext(IconStyleContext) === 'v2';
  // The eblast report uses the exact approved PNG marks (imageLogos); V2 reports use the raster marks.
  const useImages = props.imageLogos === true || v2;
  /*
   * V2 has marks for Chrome / Safari / Edge / Firefox only; every other browser (Opera, "Other",
   * "Unknown", …) draws the same generic globe. Showing them as separate cells put two identical globes
   * side by side ("5" and "3"), so on V2 they fold into ONE "Other" cell carrying their sum. A cell built
   * from a single source row stays editable; a merged one shows the total (each row is still in the
   * data, so the reconcile still balances). Legacy reports keep one cell per row, unchanged.
   */
  const MAJOR = new Set(['chrome', 'edge', 'firefox', 'safari']);
  const cells: Array<{ name: string; clicks: number; idx: number[] }> = [];
  const otherIdx: number[] = [];
  let otherClicks = 0;
  rows.forEach((r, i) => {
    if (v2 && !MAJOR.has(normaliseBrowser(s(r.name)))) { otherIdx.push(i); otherClicks += r.clicks ?? 0; }
    else cells.push({ name: s(r.name), clicks: r.clicks ?? 0, idx: [i] });
  });
  if (otherIdx.length) cells.push({ name: 'Other', clicks: otherClicks, idx: otherIdx });
  return (
    <div className={v2 ? 'card card-framed breakdown-card' : 'card'}>
      <div className="card-title">{v2 ? <Icon name="banner-browser-type" size={22} /> : <Icon name="globe" size={13} />}<span>Browser Type</span></div>
      <div className={v2 ? 'card-body browser-body' : 'card-body'}>
        {/*
          * A row of marks with the count beneath, as the mockup has it, rather than a
          * name/number table. The logo IS the label here — recognisable marks read faster
          * than words.
          */}
        <div className="browser-row">
          {cells.map((c) => (
            <div className="browser-cell" key={c.idx.join('-')}>
              {useImages
                ? <img src={browserImageFor(c.name, v2)} width={v2 ? 42 : 24} height={v2 ? 42 : 24} alt={c.name} />
                : <BrowserLogo name={c.name} size={24} />}
              <div className="browser-count num">
                {c.idx.length === 1
                  ? <V slot="rows" leaf={`[${c.idx[0]}].clicks`}>{formatInt(c.clicks)}</V>
                  : formatInt(c.clicks)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CampaignChannelsCard = ({ props, values }: BlockProps) => {
  const v2 = React.useContext(IconStyleContext) === 'v2';
  // Channel icon size. V2 enlarges the mark; the display report carries the full ~50%-larger size via a
  // prop, while the denser audio summary uses the default that its fixed page can take.
  const channelIcon = n(props.iconSize) ?? (v2 ? 68 : 30);
  const CHANNELS: Record<string, { label: string; icon: string }> = v2 ? {
    DISPLAY: { label: 'Display Ads', icon: 'channel-display' },
    EMAIL: { label: 'Email Marketing', icon: 'email' },
    EBLAST: { label: 'Eblast', icon: 'email' },
    AUDIO: { label: 'Streaming Audio', icon: 'channel-audio' },
    VIDEO: { label: 'Streaming Video', icon: 'channel-video' },
    OTT: { label: 'OTT', icon: 'channel-ott' },
    DOOH: { label: 'Digital Out-of-Home', icon: 'channel-dooh' },
    GEOFENCING: { label: 'Geofencing', icon: 'channel-geofencing' },
    RETARGETING: { label: 'Retargeting', icon: 'channel-retargeting' },
  } : {
    DISPLAY: { label: 'Display Ads', icon: 'display-ads' },
    EMAIL: { label: 'Email Marketing', icon: 'email' },
    EBLAST: { label: 'Eblast', icon: 'email' },
    AUDIO: { label: 'Streaming Audio', icon: 'headphones' },
    VIDEO: { label: 'Streaming Video', icon: 'video' },
    DOOH: { label: 'Digital Out-of-Home', icon: 'dooh-screen' },
    RETARGETING: { label: 'Retargeting', icon: 'retargeting' },
  };
  const channels = arr(values.channels).map((c) => String(c));

  /*
   * Retargeting is shown as a channel here, which it is not in the data model.
   *
   * The mockup lists it beside Display Ads because that is how the buy is described to a
   * client. It is derived rather than bound: any campaign reporting retargeted delivery
   * ran retargeting, and inventing a channel enum value for it would put a
   * presentation decision into the report data model.
   *
   * Delivery means retargeted impressions OR clicks. Keying on clicks alone hid a campaign
   * that ran retargeting (1,142 retargeted impressions) but happened to draw no clicks.
   */
  const retargeted = (n(values.retargetClicks) ?? 0) > 0 || (n(values.retargetImpressions) ?? 0) > 0;
  const shown = [...channels, ...(retargeted ? ['RETARGETING'] : [])];

  return (
    <div className={v2 ? 'card card-framed breakdown-card' : 'card'}>
      <div className="card-title">{v2 ? <Icon name="banner-campaign-channels" size={22} /> : <Icon name="monitor" size={13} />}<span>Campaign Channels</span></div>
      {/* Icon above label, side by side with a hairline between — as the mockup has it. */}
      <div className="card-body channel-row">
        {shown.map((c, i) => {
          const meta = CHANNELS[c] ?? { label: c, icon: v2 ? 'channel-display' : 'display-ads' };
          return (
            <div className="channel-cell" key={i}>
              <Icon name={meta.icon} size={channelIcon} />
              <span className="channel-label">{meta.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ------------------------------------------------------------- targeting --

const GeoTargetingMap = ({ props, values }: BlockProps) => {
  const heightIn = n(props.heightIn) ?? 5.2;
  // Preference order: the Mapbox static image baked at generation → the offline SVG (when a geoMode is
  // bound) → the legacy static-image/ZIP-band path (the white-label default, whose baseline is
  // untouched because it binds neither `mapImage` nor `geoMode`).
  const mapImage = s(values.mapImage);
  if (mapImage) return <GeoImageMap src={mapImage} values={values} heightIn={heightIn} />;
  const geoMode = s(values.geoMode);
  if (geoMode && (GEO_MODES as readonly string[]).includes(geoMode)) {
    return <GeoSvgMap mode={geoMode as GeoMode} values={values} heightIn={heightIn} cityFootnote={s(props.cityFootnote)} />;
  }
  const zips = arr(values.zips).map(String);
  const radius = n(values.radiusMiles);
  const mapId = s(values.mapAssetId);
  return (
    <div>
      {/* The static map when one was supplied (the seeded mockups have it); a generated report has
          none, and rendering the placeholder would fill the card with a 5in "geo map" box — so it
          is simply omitted and the ZIP band carries the section. */}
      {mapId ? <Asset assetId={mapId} heightIn={n(props.heightIn) ?? 5.2} label="geo map" /> : null}
      {/* A distinct ZIP row — a grey band with the "ZIP" label beside the codes, as the mockup has it. */}
      {(zips.length > 0 || radius !== null) ? (
        <div className="zip-row">
          <span className="zip-label">ZIP</span>
          <span className="zip-values">
            {zips.join(', ')}
            {radius !== null && <> (Radius: <V slot="radiusMiles">{radius}</V> miles)</>}
          </span>
        </div>
      ) : (mapId ? null : <div className="muted-note">No geographic targeting applied.</div>)}
    </div>
  );
};

/** The caption row under the map — "ZIP …", "STATES …", etc. — shared by the image and SVG maps. */
function geoBand(mode: string, values: BlockProps['values']): { label: string; value: string } | null {
  const radiusMiles = n(values.radiusMiles);
  // The radius is a ZIP / point-radius concept ONLY. A named-place buy (city/county/DMA/state) can
  // still carry a stray radiusMiles from the ad groups, but "(Radius: N miles)" there claims a
  // targeting the campaign never made — so it is appended for zip mode alone.
  const rad = radiusMiles != null ? ` (Radius: ${radiusMiles} miles)` : '';
  const list = (k: string) => arr(values[k]).map(String).join(', ');
  switch (mode) {
    case 'zip': return { label: 'ZIP', value: arr(values.zips).map(String).join(', ') + rad };
    case 'state': return { label: 'STATES', value: list('states') };
    case 'county': return { label: 'COUNTIES', value: arr(values.countyLabels).length ? list('countyLabels') : list('counties') };
    case 'city': return { label: 'CITIES', value: list('cities') };
    case 'dma': return { label: 'DMA', value: list('dmas') };
    case 'country': return { label: 'COUNTRY', value: 'Continental United States' };
    case 'geofence': return { label: 'AREA', value: 'Custom geofence' + rad };
    default: return null;
  }
}

/**
 * The map when a Mapbox Streets image was baked at generation (red overlays already drawn). It is a
 * `data:` URI, so the render inlines it with no network at print. The caption row and estimated-
 * representation note match the SVG map's, so switching a report between the two is seamless.
 */
function GeoImageMap({ src, values, heightIn }: { src: string; values: BlockProps['values']; heightIn: number }) {
  const mode = s(values.geoMode) || (arr(values.zips).length ? 'zip' : '');
  const band = geoBand(mode, values);
  return (
    <div>
      <img src={src} alt="geographic targeting map"
        style={{ display: 'block', width: '100%', height: `${heightIn}in`, objectFit: 'cover', borderRadius: '2px', border: '1px solid #d7dee6' }} />
      {band && band.value ? (
        <div className="zip-row"><span className="zip-label">{band.label}</span><span className="zip-values">{band.value}</span></div>
      ) : null}
      {/* Every mode now draws its real geometry over the streets tile — exact ZCTA / Place / county /
          DMA / state boundaries, a pin + true-radius circle for a ZIP-radius buy, the US outline for
          nationwide — so the "estimated representation" caveat no longer applies here. */}
    </div>
  );
}

/**
 * The offline, byte-reproducible geographic-targeting map — the fallback when no Mapbox image is
 * present (no token, an unsupported mode, or a failed fetch).
 *
 * `buildGeoRender` resolves the targeting to pre-projected SVG shapes/points and a viewBox zoomed to
 * them; this paints them: a faint state basemap for context, red fills for regions (state/county/DMA),
 * a bold red outline for the whole US (country), and red radius circles for ZIPs/cities. Strokes use
 * `vectorEffect="non-scaling-stroke"` so they stay crisp however far the viewBox zooms. No network, no
 * tiles — the same hand-rolled-SVG approach the charts use, so it prints deterministically (R4).
 */
function GeoSvgMap({ mode, values, heightIn, cityFootnote }: { mode: GeoMode; values: BlockProps['values']; heightIn: number; cityFootnote?: string }) {
  const zips = arr(values.zips).map(String);
  const states = arr(values.states).map(String);
  const counties = arr(values.counties).map(String);
  const cities = arr(values.cities).map(String);
  const dmas = arr(values.dmas).map(String);
  const radiusMiles = n(values.radiusMiles) ?? undefined;
  const r = buildGeoRender(mode, { zips, radiusMiles, states, counties, cities, dmas });
  const band = geoBand(mode, values);
  const missing = r.requested - r.resolved;
  // Round every emitted coordinate to 2dp: the circle radius and the label text positions are computed
  // floats (e.g. 5 × milesToPx = 4.785…) and the PDF should carry clean, deterministic numbers.
  const f2 = (v: number) => Math.round(v * 100) / 100;
  return (
    <div>
      {/* Water is the SVG's own background, so everything outside the land polygons (ocean, bays, and
          the letterboxed margins) reads as water — the way the mockup's map does. */}
      <svg viewBox={r.viewBox} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${mode} targeting map`}
        style={{ display: 'block', width: '100%', height: `${heightIn}in`, background: '#aecbe8', borderRadius: '2px',
          fontFamily: 'Roboto, Arial, sans-serif' }}>
        {/* Land */}
        {r.basePaths.map((d, i) => (
          <path key={`b${i}`} d={d} fill="#eef2ea" stroke="#9db2c6" strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
        ))}
        {/* County reference lines — faint, like the minor roads on the reference map. */}
        {r.countyLines.map((d, i) => (
          <path key={`cl${i}`} d={d} fill="none" stroke="#cdd6df" strokeWidth={0.4} vectorEffect="non-scaling-stroke" />
        ))}
        {/* Red targeting overlay */}
        {r.fills.map((d, i) => (
          <path key={`f${i}`} d={d} fill="rgba(214,40,40,0.5)" stroke="#b3231b" strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
        ))}
        {r.nationOutline ? (
          <path d={r.nationOutline} fill="rgba(214,40,40,0.05)" stroke="#c0271b" strokeWidth={2.4} vectorEffect="non-scaling-stroke" />
        ) : null}
        {r.circles.map((c, i) => (
          <circle key={`c${i}`} cx={f2(c.cx)} cy={f2(c.cy)} r={f2(c.r)} fill="rgba(214,40,40,0.4)" stroke="#b3231b" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        ))}
        {/* City labels on top: a dot and a haloed name so they stay legible over land, water or shading. */}
        {r.labels.map((l, i) => (
          <g key={`l${i}`}>
            <circle cx={f2(l.x)} cy={f2(l.y)} r={f2(r.dotR)} fill="#3b4657" stroke="#fff" strokeWidth={0.4} vectorEffect="non-scaling-stroke" />
            <text x={f2(l.x + r.dotR + r.fontSize * 0.3)} y={f2(l.y + r.fontSize * 0.34)} fontSize={f2(r.fontSize)}
              fill="#26313f" stroke="#ffffff" strokeWidth={f2(r.fontSize * 0.24)} paintOrder="stroke" strokeLinejoin="round">
              {l.text}
            </text>
          </g>
        ))}
      </svg>
      {band && band.value ? (
        <div className="zip-row">
          <span className="zip-label">{band.label}</span>
          <span className="zip-values">{band.value}</span>
        </div>
      ) : null}
      {(missing > 0 || mode === 'city') ? (
        <div className="tiny muted" style={{ marginTop: '0.05in' }}>
          {missing > 0 ? `*${missing} of ${r.requested} not shown (no boundary data). ` : ''}
          {mode === 'city' ? `*${cityFootnote || 'City targeting shown as approximate ZIP-level coverage.'}` : ''}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A US state choropleth of a count (impressions) — "where did delivery come from".
 *
 * Reuses the targeting map's state geometry but grades each state's fill by its share of the
 * busiest state, in the theme colour (via `fill-opacity` on `--color-primary`, like the day/time
 * heatmap). States with no delivery keep the light base; anything that does not resolve to a state
 * (e.g. "Undisclosed") is simply not drawn. Pure SSR SVG.
 */
const GeoHeatMap = ({ props, values }: BlockProps) => {
  const heightIn = n(props.heightIn) ?? 3.2;
  /*
   * Local / point targeting (ZIP, city, county, DMA) renders the ZOOMED offline map — the frame tight to
   * the targeted area with its radius circles, county lines and city labels — not a full-US state
   * choropleth with tiny overlay circles, which shows the wrong geographic level for how the campaign was
   * set up. National / state targeting keeps the delivery choropleth, where a state-level view is right.
   */
  const geoModeEarly = s(values.geoMode);
  if (['zip', 'city', 'county', 'dma'].includes(geoModeEarly)) {
    // The Mapbox Streets image baked at generation (same as the display report's map) when there is one;
    // the offline SVG otherwise (no MAPBOX_TOKEN, or the fetch failed).
    const mapImage = s(values.mapImage);
    if (mapImage) return <GeoImageMap src={mapImage} values={values} heightIn={heightIn} />;
    return <GeoSvgMap mode={geoModeEarly as GeoMode} values={values} heightIn={heightIn} />;
  }
  const regions = (arr(values.regions) as Array<{ state?: unknown; name?: unknown; impressions?: unknown; value?: unknown }>)
    .map((r) => ({ state: String(r.state ?? r.name ?? ''), value: Number(r.value ?? r.impressions ?? 0) }))
    .filter((r) => r.state && r.value > 0);
  const map = buildStateChoropleth(regions);
  if (map.cells.length === 0) return null;

  /*
   * The TARGETING overlay, over the delivery choropleth. `state` outlines the targeted states, `zip`
   * draws the radius circles — both in the same base coordinate space as the choropleth, so they
   * layer directly. Drawn in the accent colour so targeting (outline) reads apart from delivery
   * (filled). `country`/`geofence` add no overlay: national is the whole map, a geofence has no
   * plottable geometry here. `buildGeoRender` computes its own zoomed viewBox, which is ignored — the
   * choropleth's full-US frame is kept so targeted and delivered geography share one scale.
   */
  const geoMode = s(values.geoMode);
  const overlay = (geoMode === 'state' || geoMode === 'zip')
    ? buildGeoRender(geoMode as GeoMode, {
        states: arr(values.states).map(String),
        zips: arr(values.zips).map(String),
        radiusMiles: n(values.radiusMiles) ?? undefined,
      })
    : null;

  return (
    <div>
      <svg viewBox={map.viewBox} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Impressions by state"
        style={{ display: 'block', width: '100%', height: `${heightIn}in`, background: 'var(--color-surface)', borderRadius: '2px' }}>
        {/* Every state as the empty base. */}
        {map.basePaths.map((d, i) => (
          <path key={`b${i}`} d={d} fill="#e7ecf2" stroke="#c6d0dc" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
        ))}
        {/* Graded fills — theme colour at an opacity set by each state's intensity. */}
        {map.cells.map((c, i) => (
          <path key={`f${i}`} d={c.d} fill="var(--color-primary)" fillOpacity={0.15 + 0.85 * c.intensity}
            stroke="#9aa7b6" strokeWidth={0.5} vectorEffect="non-scaling-stroke">
            <title>{`${c.abbr}: ${c.value.toLocaleString('en-US')} impressions`}</title>
          </path>
        ))}
        <path d={map.nationOutline} fill="none" stroke="#8894a3" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
        {/*
         * Targeting overlay: targeted states outlined, or ZIP radius circles — in the report's chosen
         * theme colour (`--color-primary`, the same colour the picker sets). A white casing under the
         * outline keeps it legible over the primary-shaded delivery fills, since overlay and fill share
         * the hue. No overlay fill, so a targeted state's delivery shading is not tinted.
         */}
        {overlay?.fills.map((d, i) => (
          <path key={`tw${i}`} d={d} fill="none" stroke="#ffffff" strokeWidth={3.4}
            strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        ))}
        {overlay?.fills.map((d, i) => (
          <path key={`t${i}`} d={d} fill="none" stroke="var(--color-primary)" strokeWidth={1.8}
            strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        ))}
        {overlay?.circles.map((c, i) => (
          <circle key={`twc${i}`} cx={c.cx} cy={c.cy} r={c.r} fill="none" stroke="#ffffff"
            strokeWidth={3.4} vectorEffect="non-scaling-stroke" />
        ))}
        {overlay?.circles.map((c, i) => (
          <circle key={`tc${i}`} cx={c.cx} cy={c.cy} r={c.r} fill="var(--color-primary)" fillOpacity={0.1}
            stroke="var(--color-primary)" strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="tiny muted" style={{ marginTop: '0.05in' }}>
        Shading is impression volume by state (US Eastern).
        {overlay ? ' Outlined areas show the campaign’s geo targeting.' : ''}
      </div>
    </div>
  );
};

/**
 * Delivery cities scoped to the campaign's targeting, as a top-N table with a reconciling "Other" row.
 *
 * A ZIP / local buy delivers to a small area; its apparent out-of-area cities are IP→location resolution
 * artifacts. `scopeCitiesToTarget` keeps only the cities inside the targeted ZIP radius (or target
 * states), ranks them, shows the top `maxRows` and rolls the rest — plus everything outside the target —
 * into "Other". Renders the bare table; the template wraps it in a SectionCard for the heading.
 */
const GeoCityBreakdown = ({ props, values }: BlockProps) => {
  const cities = (arr(values.cities) as Array<{ city?: unknown; state?: unknown; impressions?: unknown }>)
    .map((c) => ({ city: String(c.city ?? ''), state: String(c.state ?? ''), impressions: Number(c.impressions ?? 0) }))
    .filter((c) => c.city && c.impressions > 0);
  const { inside, outsideTotal } = scopeCitiesToTarget(cities, {
    geoMode: s(values.geoMode),
    zips: arr(values.zips).map(String),
    radiusMiles: n(values.radiusMiles) ?? undefined,
    states: arr(values.states).map(String),
  });
  inside.sort((a, b) => b.impressions - a.impressions);
  const topN = n(props.maxRows) ?? 10;
  const top = inside.slice(0, topN);
  const other = inside.slice(topN).reduce((sum, c) => sum + c.impressions, 0) + outsideTotal;
  if (!top.length) return <div className="muted-note">No city-level delivery resolved inside the targeted area.</div>;
  return (
    <table className="zebra">
      <tbody>
        {top.map((c, i) => (
          <tr key={i}><td>{c.city}, {c.state}</td><td className="n">{formatInt(c.impressions)}</td></tr>
        ))}
        {other > 0 && <tr><td>Other</td><td className="n">{formatInt(other)}</td></tr>}
      </tbody>
    </table>
  );
};

/** A short "Label:" / "Label-" prefix → bold label + value; otherwise the line unchanged. */
function labelled(line: string): React.ReactNode {
  const m = /^([A-Za-z][A-Za-z/ ]{1,22}?)\s*[:–—-]\s+(\S.*)$/.exec(line);
  return m ? <><strong>{m[1]}:</strong> {m[2]}</> : line;
}

const AudienceTargetingCard = ({ values }: BlockProps) => {
  /*
   * The demographic arrives from the portal as prose and is flattened upstream into one note joined by
   * " — " (stripHtml), which lost its line structure and read as a single run-on bullet under an
   * industry-jargon heading. Recover the lines here and format: the campaign / flight name as a bold lead,
   * the rest as bullets with any short "Label:" prefix bolded. No heading. One stored note or several both
   * flatten the same way, so this reads correctly on existing reports without regeneration.
   */
  const lines = arr(values.items)
    .flatMap((it) => String(it).split(/\s+[–—]\s+/))
    .map((l) => l.trim().replace(/[;,·\s]+$/, ''))
    .filter(Boolean);
  if (lines.length === 0) {
    // Shown because the reviewer left the section on; the campaign simply targeted broadly.
    return <div className="muted-note">Broad reach — no demographic targeting applied.</div>;
  }
  const [lead, ...rest] = lines;
  const leadM = /^([A-Za-z][A-Za-z/ ]{1,22}?)\s*[:–—-]\s+(\S.*)$/.exec(lead);
  return (
    <div className="audience-list">
      <div className="audience-lead">{leadM ? <><strong>{leadM[1]}:</strong> {leadM[2]}</> : <strong>{lead}</strong>}</div>
      {rest.length > 0 ? (
        <ul className="audience-bullets">
          {rest.map((line, i) => <li key={i}>{labelled(line)}</li>)}
        </ul>
      ) : null}
    </div>
  );
};

const AttributeCards = ({ props, values }: BlockProps) => {
  const cards = arr(values.cards) as Array<{ label?: string; value?: unknown; icon?: string }>;
  const cols = n(props.columns) ?? 3;
  const v2 = React.useContext(IconStyleContext) === 'v2';
  /*
   * `header` variant: a dark heading bar (icon + label) over a white body carrying the value — the
   * streaming-audio mockup's Language / Age Group / Gender cards. The default keeps the plain
   * value-over-label tile the other channels use.
   */
  if (props.variant === 'header') {
    return (
      <div className="kpi" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {cards.map((c, i) => (
          <div className="card attr-card" key={i}>
            <div className="card-title">
              {c.icon ? <Icon name={String(c.icon)} size={v2 ? 22 : 13} /> : null}<span>{s(c.label)}</span>
            </div>
            <div className="attr-value"><V slot="cards" leaf={`[${i}].value`}>{String(c.value ?? '')}</V></div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="kpi" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {cards.map((c, i) => (
        <div className="kpi-tile" key={i}>
          <div style={{ fontWeight: 700 }}>
            <V slot="cards" leaf={`[${i}].value`}>{String(c.value ?? '')}</V>
          </div>
          <div className="kpi-label">{s(c.label)}</div>
        </div>
      ))}
    </div>
  );
};

const TopSitesList = ({ props, values }: BlockProps) => {
  // Cap at 10 so the block reads as the mockup's 2-column × 5-row list (column-count:2 fills 5 rows).
  const sites = arr(values.sites).slice(0, n(props.max) ?? 10);
  if (sites.length === 0) {
    // Delivery is not yet consolidated by publisher, so there are no sites to list. The reviewer
    // asked to see the section, so it renders with an honest note rather than disappearing.
    return <div className="muted-note">No site-level delivery data available for this campaign.</div>;
  }
  return (
    <ul className="plain list-cols" style={{ columnCount: n(props.columns) ?? 2 }}>
      {sites.map((site, i) => (
        <li key={i}><V slot="sites" leaf={`[${i}]`}>{String(site)}</V></li>
      ))}
    </ul>
  );
};

const PinMap = ({ props, values }: BlockProps) => (
  <Asset assetId={s(values.mapAssetId)} heightIn={n(props.heightIn) ?? 5.0} label="placement map" />
);

// ---------------------------------------------------------------- detail --

const CreativeDetailList = ({ props, values }: BlockProps) => {
  const creatives = arr(values.creatives) as Array<Record<string, unknown>>;
  const showClicks = props.showClicks !== false;

  /*
   * A card per creative, with the artwork — the mockup's campaign details page.
   *
   * This was a four-column table, which is a reasonable way to present creatives and is
   * not what was approved. The artwork is the point of the page: a client recognises
   * their banner and reads the clicks against it, and a row saying "cr1 · 728 x 90"
   * gives them an internal id instead.
   *
   * Each creative is atomic, so a card is never split across a page break with its
   * figures orphaned from its image.
   */
  /*
   * `icons` variant: each fact carries an outlined ring badge (image / globe / cursor), the
   * treatment the streaming-audio mockup uses for the single-creative identity block on the
   * Streaming Audio Summary page. The default is the plain dt/dd list the campaign-details
   * showcase uses.
   */
  const iconsMode = props.factsVariant === 'icons';
  // V2 uses the raster creative-fact marks; legacy keeps the inline-SVG image/globe/cursor glyphs.
  const v2 = React.useContext(IconStyleContext) === 'v2';
  const fact = {
    dims: v2 ? 'audio-image-dimensions' : 'image', url: v2 ? 'audio-destination-url' : 'globe',
    clicks: v2 ? 'audio-clicks' : 'cursor',
  };
  const factSize = v2 ? 28 : 14;
  return (
    // `data-rows`: each creative card is a paginator row, so a long ad group breaks onto the next page
    // (title dropped on continuations) instead of one tall atomic block running into the footer.
    <div className="creative-list" data-rows>
      {creatives.map((c, i) => (
        <div className={`creative-item${iconsMode ? ' creative-item-icons' : ''}`} key={i}>
          <div className="creative-art">
            <Asset assetId={s(c.assetId)} heightIn={n(props.maxHeightIn) ?? 1.05} label="creative" />
          </div>
          {iconsMode ? (
            <dl className="meta-rows creative-facts-icons">
              <div className="meta-row">
                <span className="icon-badge ring"><Icon name={fact.dims} size={factSize} /></span>
                <dt>Image Dimensions:</dt>
                <dd><V slot="creatives" leaf={`[${i}].dimensions`}>{s(c.dimensions, '—')}</V></dd>
              </div>
              <div className="meta-row">
                <span className="icon-badge ring"><Icon name={fact.url} size={factSize} /></span>
                <dt>Destination URL:</dt>
                <dd className="url"><V slot="creatives" leaf={`[${i}].destinationUrl`}>{s(c.destinationUrl, '—')}</V></dd>
              </div>
              {showClicks && (
                <div className="meta-row">
                  <span className="icon-badge ring"><Icon name={fact.clicks} size={factSize} /></span>
                  <dt>Clicks:</dt>
                  <dd className="num"><V slot="creatives" leaf={`[${i}].clicks`}>{renderMetric(c.clicks, 'tbd')}</V></dd>
                </div>
              )}
            </dl>
          ) : (
            <dl className="creative-facts">
              <div><dt>Image Dimensions:</dt><dd>
                <V slot="creatives" leaf={`[${i}].dimensions`}>{s(c.dimensions, '—')}</V>
              </dd></div>
              <div className="stacked">
                <dt>Destination URL:</dt>
                <dd className="url">
                  <V slot="creatives" leaf={`[${i}].destinationUrl`}>{s(c.destinationUrl, '—')}</V>
                </dd>
              </div>
              {showClicks && (
                <div><dt>Clicks:</dt><dd className="num">
                  <V slot="creatives" leaf={`[${i}].clicks`}>{renderMetric(c.clicks, 'tbd')}</V>
                </dd></div>
              )}
            </dl>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * The flowing table behind the DOOH venue list.
 *
 * `showingOf` is not cosmetic. D42 caps the placement list at 100 rows; a capped list
 * presented as if it were complete is a misrepresentation, so the disclosure renders
 * whenever the row set was actually trimmed.
 */
const PaginatedTable = ({ props, values, manifest }: BlockProps) => {
  /*
   * `format` lets a column display a RAW numeric value (int / pct) rather than a pre-formatted string
   * from the binding — which is what makes a cell editable: an editable path must be a plain data path
   * (`clicks`), and `$formatNumber(clicks, …)` in the binding is a function call the editable-path
   * detector rejects. So editable figure columns bind the raw path and format here; `editable: false`
   * renders a derived column (a rate) as plain text with no edit affordance. Absent `format`/`editable`
   * preserves the old behaviour: the binding's already-formatted string, wrapped for editing.
   */
  let columns: Array<{ key: string; label: string; widthPct?: number; breakWords?: boolean;
    format?: 'int' | 'pct' | 'text'; editable?: boolean }> = [];
  try { columns = JSON.parse(s(props.columns, '[]')); } catch { columns = []; }
  const rows = arr(values.rows) as Array<Record<string, unknown>>;
  const max = n(props.maxRows) ?? 100;
  const shown = rows.slice(0, max);
  const total = n(values.totalRows) ?? rows.length;
  const capped = total > shown.length;
  /*
   * One line per cell, ellipsised — for tables inside an atomic container (a Columns layout),
   * where a wrapping value would inflate the row and overflow the fixed-height block. `max-width: 0`
   * is the standard trick that lets a table cell's `text-overflow` engage: it forces the cell to
   * take its share from `width`/`widthPct` rather than growing to fit the text.
   */
  const clip: React.CSSProperties | undefined = props.ellipsizeCells === true
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }
    : undefined;
  // Force-break long unbroken strings (e.g. destination URLs with no spaces) so a wide cell wraps within
  // its column instead of overflowing and shoving the other columns off the page. Per-column via
  // `breakWords` in the column spec, or table-wide via the block prop.
  const breakAll: React.CSSProperties = { wordBreak: 'break-all', whiteSpace: 'normal' };
  const cellStyle = (c: { breakWords?: boolean }): React.CSSProperties | undefined =>
    clip ?? ((c.breakWords || props.breakWords === true) ? breakAll : undefined);
  /*
   * Removable rows (apps / geo targets): in the builder only (EditContext present), each row gets a "×"
   * that carries the dimension + the row's key. A delegated listener on the canvas turns that into a
   * remove call; materialise then redistributes the row's impressions across the survivors. The PDF has no
   * EditContext, so the column never prints.
   */
  const edit = React.useContext(EditContext);
  const removeDim = s(props.removeDim);
  const removeKeyCol = s(props.removeKeyCol) || 'id';
  const showRemove = !!edit && !!removeDim;
  /*
   * The `data-rows` / `data-row-header` / `data-row-caption` hooks are what the
   * paginator measures and splits on. They live here rather than in the paginator
   * because only the block knows which of its elements is the repeatable header and
   * which run of children may be divided across pages.
   */
  return (
    <div>
      <table className={props.zebra !== false ? 'zebra' : undefined}
        style={props.fixedLayout === true ? { tableLayout: 'fixed', width: '100%' } : undefined}>
        <thead data-row-header data-repeat-header={String(manifest.repeatHeaderOnBreak)}>
          <tr>
            {showRemove && <th aria-label="Remove" style={{ width: '1.4em' }} />}
            {columns.map((c) => <th key={c.key} style={c.widthPct ? { width: `${c.widthPct}%` } : undefined}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody data-rows>
          {shown.map((r, i) => (
            <tr key={i}>
              {showRemove && (
                <td className="row-remove-cell">
                  <button
                    type="button" className="row-remove" title="Remove this row"
                    data-remove-dim={removeDim} data-remove-key={String(r[removeKeyCol] ?? '')}
                  >×</button>
                </td>
              )}
              {columns.map((c) => {
                const display = c.format && c.format !== 'text'
                  ? renderMetric(r[c.key] as number | null | undefined, manifest.unavailableRendering,
                    c.format === 'pct' ? (x) => formatPct(x) : formatInt)
                  : String(r[c.key] ?? '');
                return (
                  <td key={c.key} style={cellStyle(c)}>
                    {c.editable === false
                      ? display
                      : <V slot="rows" leaf={`[${i}].${c.key}`}>{display}</V>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {props.showingOf !== false && capped && (
        <div className="tiny muted" data-row-caption style={{ marginTop: '0.05in' }}>
          Showing {formatInt(shown.length)} of {formatInt(total)}.
        </div>
      )}
    </div>
  );
};

const VenueTypeSummary = ({ props, values }: BlockProps) => {
  const rows = arr(values.rows) as Array<{ venueType?: string; screens?: number }>;
  const total = n(values.total);
  return (
    <div>
      <ul className="plain list-cols" style={{ columnCount: n(props.columns) ?? 2 }}>
        {rows.map((r, i) => (
          <li key={i}>
            <V slot="rows" leaf={`[${i}].venueType`}>{s(r.venueType)}</V>
            {' — '}
            <V slot="rows" leaf={`[${i}].screens`}>{formatInt(r.screens ?? 0)}</V>
          </li>
        ))}
      </ul>
      {/* `noun` names what was counted — "matching placements" pre-flight; "delivering screens" /
          "targeted screens" post-flight, where "placements" would read as the pre-flight projection. */}
      {total !== null && <div className="tiny muted">Across all {formatInt(total)} {s(props.noun) || 'matching placements'}.</div>}
    </div>
  );
};

// ----------------------------------------------------------------- chart --

const SeriesChart = ({ props, values }: BlockProps) => {
  const maxCategories = n(props.maxCategories) ?? 40;
  const allCategories = arr(values.categories).map(String);
  const categories = allCategories.slice(0, maxCategories);
  const series = (arr(values.series) as Array<{ name?: string; values?: unknown }>).map((s) => ({
    name: typeof s.name === 'string' ? s.name : undefined,
    values: arr(s.values).slice(0, maxCategories).map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)),
  }));
  if (categories.length === 0 || series.length === 0) return null;
  return (
    <SeriesChartSvg
      categories={categories}
      series={series}
      chartType={(props.chartType === 'line' ? 'line' : 'bar')}
      heightIn={n(props.heightIn) ?? 2.6}
      xLabel={typeof props.xLabel === 'string' ? props.xLabel : undefined}
      yLabel={typeof props.yLabel === 'string' ? props.yLabel : undefined}
      valueFormat={props.valueFormat === 'pct' ? 'pct' : 'int'}
      showLegend={props.showLegend !== false}
      omitted={allCategories.length - categories.length}
    />
  );
};

const PieChart = ({ props, values }: BlockProps) => {
  const raw = (arr(values.slices) as Array<{ label?: unknown; value?: unknown; city?: unknown; clicks?: unknown }>)
    // Accept either {label,value} or the data model's {city,clicks} without forcing
    // every template to restructure in an expression.
    .map((s) => ({
      label: String(s.label ?? s.city ?? ''),
      value: typeof s.value === 'number' ? s.value : typeof s.clicks === 'number' ? s.clicks : 0,
    }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  if (raw.length === 0) return null;

  /*
   * A row the data already labels "Other…" (the CTV builder's "Other apps" remainder) is not a named
   * slice: it always folds into the pie's own Other bucket, so the chart never shows two "Other" slices.
   */
  const isOther = (s: { label: string }) => /^other\b/i.test(s.label);
  const named = raw.filter((s) => !isOther(s));
  const preOther = raw.filter(isOther);
  const max = n(props.maxSlices) ?? 8;
  const shown = named.slice(0, max);
  const rest = [...named.slice(max), ...preOther];
  return (
    <PieChartSvg
      slices={shown}
      heightIn={n(props.heightIn) ?? 2.6}
      showLegend={props.showLegend !== false}
      // Folded, not dropped: a pie whose visible slices do not sum to the total
      // misstates every proportion on it.
      otherValue={rest.reduce((t, s) => t + s.value, 0)}
      // An unnamed remainder means the count of apps in "Other" is unknown — label it plain "Other".
      otherCount={preOther.length ? 0 : rest.length}
    />
  );
};

/**
 * Day × hour heatmap — impressions (or any count) over a 7×24 matrix.
 *
 * Rows are days (Sun…Sat), columns hours (0-23). Cell shade scales with the value against the
 * busiest cell, so the reader sees WHEN delivery concentrated at a glance. Pure SSR SVG, so it
 * prints with no script (R4). The matrix is the data model's `viewsByDayHour`.
 */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DayHourHeatmap = ({ props, values }: BlockProps) => {
  const matrix = arr(values.matrix) as unknown[];
  // Normalise to exactly 7×24 of finite numbers; bail if there is nothing to show.
  const grid: number[][] = Array.from({ length: 7 }, (_, d) => {
    const row = arr((matrix[d] as unknown) ?? []);
    return Array.from({ length: 24 }, (_, h) => {
      const v = row[h];
      return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
    });
  });
  const max = Math.max(0, ...grid.flat());
  if (max === 0) return null;

  /*
   * In the builder (EditContext present), each cell is editable: it carries the data path of its
   * own value (`<matrix path>.<day>.<hour>`) plus `edit-kind="heat"` and the current max, which the
   * builder reads to open a 5-level heat slider. In the PDF (no EditContext) these attributes are
   * absent, so print is unchanged. `matrixPath` is the source path of the `matrix` binding.
   */
  const edit = React.useContext(EditContext);
  const sources = React.useContext(SourceContext);
  const matrixPath = edit ? (sources.matrix?.dataPath ?? null) : null;

  const W = 720, padL = 42, padTop = 20, padBottom = 18, gap = 1.5;
  const cols = 24, rows = 7;
  const cellW = (W - padL - 6) / cols;
  const cellH = 24;
  const H = padTop + rows * cellH + padBottom;

  // Cells are the THEME primary at varying opacity, so the heatmap follows the report's themeColor.
  // A non-zero cell starts at a visible floor so a single impression still reads; empty cells are a
  // light neutral. `fill` is the CSS var; only the opacity carries the intensity.
  const opacity = (v: number): number => (v <= 0 ? 0 : 0.12 + 0.88 * (v / max));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
         style={{ display: 'block', maxWidth: '100%' }}
         aria-label="Impressions by day of week and hour of day">
      {/* Hour axis labels, every 3 hours to stay legible */}
      {Array.from({ length: cols }, (_, h) => (h % 3 === 0 ? (
        <text key={`h${h}`} x={padL + h * cellW + cellW / 2} y={padTop - 7}
              textAnchor="middle" fontSize={9} fill="#777">{h}</text>
      ) : null))}
      {grid.map((row, d) => (
        <g key={`d${d}`}>
          <text x={padL - 8} y={padTop + d * cellH + cellH / 2 + 3}
                textAnchor="end" fontSize={10} fill="#555">{DAY_LABELS[d]}</text>
          {row.map((v, h) => {
            // Bracket notation to match `editablePaths` (which walks the matrix as `[d][h]`) — a
            // dot-index path would be rejected as "not editable" by the override guard.
            const cellPath = matrixPath ? `${matrixPath}[${d}][${h}]` : null;
            const set = cellPath ? edit!.overridden(cellPath) : false;
            return (
              <rect key={`c${d}-${h}`} x={padL + h * cellW + gap / 2} y={padTop + d * cellH + gap / 2}
                    width={cellW - gap} height={cellH - gap} rx={2}
                    // --color-value: the primary when it reads on white, else a darker brand shade — a light
                    // white-label primary (the FBI's lime) left the faint cells nearly invisible.
                    fill={v > 0 ? 'var(--color-value)' : '#eef1f5'} fillOpacity={v > 0 ? opacity(v) : 1}
                    // Builder only: make the cell a click target for the heat slider. An adjusted cell
                    // gets an accent outline so the reviewer sees which cells were set by hand.
                    {...(cellPath ? {
                      'data-edit-path': cellPath, 'data-edit-kind': 'heat',
                      'data-edit-max': String(max), 'data-edit-value': String(v),
                      style: { cursor: 'pointer' },
                      stroke: set ? 'var(--color-accent)' : undefined,
                      strokeWidth: set ? 1.5 : undefined,
                    } : {})}>
                <title>{`${DAY_LABELS[d]} ${h}:00 — ${v.toLocaleString('en-US')} impressions`}</title>
              </rect>
            );
          })}
        </g>
      ))}
      {/* Caption: which timezone the hours are in, so "hour 20" is unambiguous */}
      <text x={padL} y={H - 5} fontSize={9} fill="#999">
        {s(props.caption) || 'Hour of day (US Eastern) · shade = impressions'}
      </text>
    </svg>
  );
};

// --------------------------------------------------------------- content --

const RichTextBlock = ({ props, values }: BlockProps) => (
  // D21: author markup is inserted as-is. Acceptable only while templates are authored
  // by engineers in code — there is still no sanitiser.
  <div className={s(props.size) === 'lead' ? 'lead' : s(props.size) === 'small' ? 'small-copy' : s(props.size) === 'note' ? 'footnote' : undefined}
       dangerouslySetInnerHTML={{ __html: s(values.html) }} />
);

const CustomHtml = ({ props }: BlockProps) => (
  <div dangerouslySetInnerHTML={{ __html: s(props.html) }} />
);

const Columns = ({ props, children }: BlockProps) => {
  /*
   * Unequal columns. `.row > *` gives every child `flex: 1 1 0` (equal), which is right for a
   * symmetric split but not for, say, a narrow creative beside a wide table. `weights` — a
   * comma-separated list like "2,3" — overrides the flex-grow per column, so the columns take
   * space in that ratio. Each child is wrapped so the inline flex beats the stylesheet rule;
   * `min-width: 0` keeps a long, wrapping cell from forcing its column wider than its share.
   */
  const weights = s(props.weights)
    .split(',').map((w) => Number(w.trim())).filter((w) => Number.isFinite(w) && w > 0);
  const kids = React.Children.toArray(children);
  return (
    <div className="row" style={{ alignItems: props.align === 'start' ? 'flex-start' : 'stretch' }}>
      {weights.length
        ? kids.map((child, i) => (
            <div key={i} style={{ flex: `${weights[i] ?? 1} 1 0`, minWidth: 0 }}>{child}</div>
          ))
        : children}
    </div>
  );
};

const SectionCard = ({ props, values, children }: BlockProps) => {
  // A right-aligned label in the heading — the mockup's "Ad Group: Display 1" on the campaign
  // details page. Bound (`rightText`) when it names a repeated item, or a static prop.
  const right = s(values.rightText) || s(props.rightText);
  const v2 = React.useContext(IconStyleContext) === 'v2';
  return (
    <div className="card">
      {/* Every card heading in the mockups pairs a small glyph with the label. */}
      <div className={`card-title${props.titlecase === true ? ' titlecase' : ''}`}>
        <Icon name={s(props.icon) || undefined} size={v2 ? 20 : 13} />
        {/* A bound `title` (e.g. "Impressions by Week" on a long flight) wins over the static prop. */}
        <span>{s(values.title) || s(props.title)}</span>
        {right ? <span className="card-title-right">{right}</span> : null}
      </div>
      <div className="card-body stack">{children}</div>
    </div>
  );
};

const ProvenanceNote = ({ props }: BlockProps) => (
  <div className="provenance">{s(props.template)}</div>
);

// ---------------------------------------------------------------------------

export const BLOCK_COMPONENTS: Record<string, React.ComponentType<BlockProps>> = {
  PageHeader, PageFooter,
  CampaignMetaCard, CreativePreview,
  KpiTileRow, DeliveryStrip, MetricTrio, ContractedVsActual,
  DeviceCategoryCard, DeviceDetailsTable, BrowserTypeCard, CampaignChannelsCard,
  GeoTargetingMap, GeoHeatMap, GeoCityBreakdown, AudienceTargetingCard, AttributeCards, TopSitesList, PinMap,
  CreativeDetailList, PaginatedTable, VenueTypeSummary,
  SeriesChart, PieChart, DayHourHeatmap,
  RichTextBlock, CustomHtml, Columns, SectionCard, ProvenanceNote,
};
